import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { useMemo } from 'react';

type Preset = 'esteAno' | 'anoPassado' | 'ultimos30' | 'ultimos90' | 'custom';

const PRESETS: Record<
  Exclude<Preset, 'custom'>,
  () => { inicio: Moment; fim: Moment }
> = {
  esteAno: () => ({
    inicio: moment().startOf('year'),
    fim: moment().endOf('year'),
  }),
  anoPassado: () => ({
    inicio: moment().subtract(1, 'year').startOf('year'),
    fim: moment().subtract(1, 'year').endOf('year'),
  }),
  ultimos30: () => ({
    inicio: moment().subtract(30, 'days'),
    fim: moment(),
  }),
  ultimos90: () => ({
    inicio: moment().subtract(90, 'days'),
    fim: moment(),
  }),
};

const PRESET_LABELS: Record<Preset, string> = {
  esteAno: 'Este ano',
  anoPassado: 'Ano passado',
  ultimos30: '30 dias',
  ultimos90: '90 dias',
  custom: 'Personalizado',
};

function detectPreset(inicio?: Date, fim?: Date): Preset {
  if (!inicio || !fim) return 'custom';

  const mIni = moment(inicio).startOf('day');
  const mFim = moment(fim).endOf('day');

  for (const key of Object.keys(PRESETS) as Exclude<Preset, 'custom'>[]) {
    const { inicio: pIni, fim: pFim } = PRESETS[key]();
    if (mIni.isSame(pIni, 'day') && mFim.isSame(pFim, 'day')) {
      return key;
    }
  }
  return 'custom';
}

type Props = {
  dataInicio?: Date;
  dataFim?: Date;
  onChange: (inicio?: Date, fim?: Date) => void;
};

export default function PeriodoSelect({
  dataInicio,
  dataFim,
  onChange,
}: Props) {
  const presetAtivo = useMemo(
    () => detectPreset(dataInicio, dataFim),
    [dataInicio, dataFim],
  );

  const handlePresetChange = (
    _: React.MouseEvent<HTMLElement>,
    preset: Preset | null,
  ) => {
    if (preset && preset !== 'custom') {
      const { inicio, fim } = PRESETS[preset]();
      onChange(inicio.toDate(), fim.toDate());
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box
        display="flex"
        flexDirection="row"
        gap={1}
        alignItems="center"
        flexWrap="wrap"
      >
        <DatePicker
          label="Início"
          value={dataInicio ? moment(dataInicio) : null}
          onChange={(v) => {
            if (v) {
              const fim =
                dataFim && moment(dataFim).isAfter(v) ? dataFim : undefined;
              onChange(v.toDate(), fim);
            } else {
              onChange(undefined, undefined);
            }
          }}
          slotProps={{
            textField: {
              size: 'small',
              // Data é dado: mono tabular para os dois campos ficarem alinhados.
              sx: (theme) => ({
                width: '16ch',
                '& .MuiInputBase-input': {
                  fontFamily: theme.typography.dataMono.fontFamily,
                  fontVariantNumeric: 'tabular-nums',
                },
              }),
            },
          }}
        />
        <DatePicker
          label="Fim"
          value={dataFim ? moment(dataFim) : null}
          onChange={(v) => {
            if (v) {
              const inicio =
                dataInicio && moment(dataInicio).isBefore(v)
                  ? dataInicio
                  : undefined;
              onChange(inicio, v.toDate());
            } else {
              onChange(undefined, undefined);
            }
          }}
          slotProps={{
            textField: {
              size: 'small',
              // Data é dado: mono tabular para os dois campos ficarem alinhados.
              sx: (theme) => ({
                width: '16ch',
                '& .MuiInputBase-input': {
                  fontFamily: theme.typography.dataMono.fontFamily,
                  fontVariantNumeric: 'tabular-nums',
                },
              }),
            },
          }}
        />
        <ToggleButtonGroup
          value={presetAtivo}
          exclusive
          onChange={handlePresetChange}
          size="small"
        >
          {(Object.keys(PRESETS) as Exclude<Preset, 'custom'>[]).map((key) => (
            <ToggleButton key={key} value={key} sx={{ px: 1.5 }}>
              {PRESET_LABELS[key]}
            </ToggleButton>
          ))}
          <ToggleButton value="custom" sx={{ px: 1.5 }}>
            {PRESET_LABELS.custom}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </LocalizationProvider>
  );
}
